// src/components/Products/VariationCard.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { resolveImageUrls, buildAssetUrl, retryThenPlaceholder } from '../../utils/imageResolver';
import PhotoCarousel from '../common/PhotoCarousel';
import { NO_PHOTO_SRC } from '../../constants/noPhoto';
import { addToCart } from '../../services/api';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import useNotification from '../../hooks/useNotification';
import { CartErrorMessages } from '../Utils/errorUtils';
import ConfirmationModal from '../ConfirmationModal';
import '../../styles/products.css';
import { formatSpeciesName } from '../../utils/scientificName';

const VariationCard = ({ variation, product, showMorphNames, restockSubscribed, onToggleRestockNotify }) => {
  const [quantity, setQuantity] = useState(1);
  // Cowork C2/C3 — the clamp below was silent: typing 4 with 3 available
  // just rewrote the box to 3. Say why, on the card, until the next
  // change.
  const [qtyNote, setQtyNote] = useState(null);
  const [adding, setAdding] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [restockOptedOutPrefsUrl, setRestockOptedOutPrefsUrl] = useState(null);

  const { refreshCart, getTotalQuantityForVariation, addItem } = useCart();
  const { isAuthenticated } = useAuth();
  const { showNotification } = useNotification();

  const variationType = variation.variation_type?.name || 'Unknown';
  const morphName = variation.morph?.name || '';

  // Cascade: variation -> morph -> species
  const v = variation;
  const vImg = resolveImageUrls(v);
  const mImg = resolveImageUrls(v?.morph);
  const sImg = resolveImageUrls(v?.species || product?.species);

  // Guard for "Normal/null morph": only use morph image if it actually exists
  const hasMorphImage = Boolean(mImg?.url);
  const chosen = vImg?.url || (hasMorphImage ? mImg?.url : null) || sImg?.url || null;
  // S2-01.2: variation's own gallery (from API) → card carousel; falls
  // back to the single cascade image. Manual-only on cards (no auto).
  // Card-size images prefer the 400px thumb rendition (thumb_url falls
  // back to the original server-side).
  const cardGallery = (Array.isArray(v?.gallery) && v.gallery.length > 0)
    ? v.gallery.map((g) => ({
        url: buildAssetUrl(g?.thumb_url || g?.url),
        thumbUrl: buildAssetUrl(g?.thumb_url || g?.url),
        credit: (g?.credit || '').trim() || null,
        creditInfo: g?.credit_info || null,  // #110e — license pill fallback
      })).filter((g) => g.url)
    : [];
  // Audit item 9 (2026-08-14): before surrendering to the no-photo tile,
  // borrow the product page's own gallery (species cover+gallery,
  // rendition-first server-side) — a card must never show a placeholder
  // while the hero right above it is painting real species photos.
  const productGalleryFallback = (Array.isArray(product?.gallery) && product.gallery[0])
    ? buildAssetUrl(product.gallery[0].thumb_url || product.gallery[0].url)
    : null;
  const variationImageSrc = (cardGallery[0]?.url) || buildAssetUrl(chosen) || productGalleryFallback;

  const priceValue = variation.latest_price?.price;
  const price = priceValue ? `$${parseFloat(priceValue).toFixed(2)}` : 'N/A';

  const displayMorph = showMorphNames
    ? morphName?.trim() || 'Normal'
    : null;

  const title = displayMorph
    ? `${variationType} — ${displayMorph}`
    : variationType;

  const isAvailable = variation.is_for_sale && variation.count > 0;
  const remaining = variation.count - getTotalQuantityForVariation(variation.id);
  // Cowork QA F1 — the ONE test that decides Add-to-Cart vs Notify-Me.
  // A non-purchasable card must not advertise a count: "Count: 4 of 4"
  // above a "Notify Me When Available" button reads as four in stock
  // when the variation has no active price / For Sale is off, and the
  // shopper cannot buy a single one of them. Hoisted so the count line
  // and the action row can never disagree about what "available" means.
  const isPurchasable = isAvailable && remaining > 0;

  /* Availability pill (competitive benchmark 2026-08) — the field's
     standard badge: stock at zero → "Sold Out"; purchasable but scarce
     (count <= 2) → "Only N left" (N = remaining, so the pill and the
     "Count: N of M" line below can never disagree for a shopper with
     some already in cart). Neutral dark — availability is a fact, not a
     deal (sale badges keep orange, type chips keep blue; both settled).
     ONE pill max in the top-left image slot: this card has no info chip,
     so the pill is the only occupant. Not-for-sale-with-stock cards get
     NO pill — "Sold Out" would be a lie; they already dim + Notify Me. */
  const availabilityPill = variation.count <= 0
    ? 'Sold Out'
    : (isPurchasable && remaining <= 2 ? `Only ${remaining} left` : null);

  /* A single-animal individual listing (#101, 2026-08-14). It used to
     wear an overlay badge here ("One of a kind", then "This exact
     animal"); the badge is gone (prod 2026-10-05). The predicate stays
     because it still keeps the "Only 1 left" scarcity pill off a listing
     that is one animal by definition. */
  const individualAnimals = Array.isArray(variation.individual_animals)
    ? variation.individual_animals
    : [];
  const isSingleAnimalListing = variation.sales_mode === 'individual'
    && individualAnimals.length === 1
    // adv-review SF1: the COUNT must agree — 1 listed of 2 counted must
    // not claim uniqueness above "Count: 2 of 2" (and must keep the
    // accurate scarcity pill instead).
    && variation.count === 1;

  const handleAddToCart = async () => {
    if (quantity > remaining) {
      showNotification(`Only ${remaining} left available.`, "error");
      return;
    }

    setAdding(true);
    try {
      // ⚠️ NO SECOND "ADDED" (Cowork run 33, D6). `addItem` already says it
      // — the cart's toast ("Added 1 Leopard Gecko to cart"), which is the
      // `role="status"` / `aria-live="polite"` region screen readers hear.
      // This card ALSO raised a page notice ("Added 1 to cart"): the faint
      // duplicate box top-right, and not a live region, so a hidden copy
      // would have helped no one. Worse, on a guest's stock refusal
      // `addItem` returns without throwing, and this line then painted
      // "Added" over the refusal it had just shown.
      await addItem(variation, quantity);
    } catch (error) {
      const errorCode = error?.response?.data?.error_code;
      const message = CartErrorMessages[errorCode] || "Failed to add to cart.";
      showNotification(message, "error");
    } finally {
      setAdding(false);
    }
  };

  const handleNotifyMe = async () => {
    setNotifyLoading(true);
    try {
      await onToggleRestockNotify();
      showNotification(
        restockSubscribed ? 'Restock notification removed' : 'You\'ll be notified when this is back in stock!',
        'success'
      );
    } catch (error) {
      const errorCode = error?.response?.data?.error_code;
      if (errorCode === 'RESTOCK_OPTED_OUT') {
        const prefsUrl = error?.response?.data?.preferences_url;
        if (prefsUrl) {
          setRestockOptedOutPrefsUrl(prefsUrl);
        } else {
          showNotification('Restock notifications are disabled in your email preferences.', 'error');
        }
      } else {
        const msg = error?.response?.data?.error || 'Failed to update notification preference';
        showNotification(msg, 'error');
      }
    } finally {
      setNotifyLoading(false);
    }
  };

  const handleGoToPreferences = () => {
    const url = restockOptedOutPrefsUrl;
    setRestockOptedOutPrefsUrl(null);
    if (url) window.location.href = url;
  };

  return (
    <>
    <div className="variation-card card">
      {/* Audit item 10c (2026-08-14): pills anchor to .listing-media-box
          (position:relative) instead of the .card, so they are pinned to
          the IMAGE BOX itself — indicator/caption reflow inside the
          carousel can never collide or displace them. */}
      <div className="listing-media-box">
        {/* The single-animal listing wears NO overlay any more — the
            "This exact animal" badge is gone (operator, prod 2026-10-05:
            "every time we try to get clever there it just ends up looking
            dumb"). It still suppresses the "Only 1 left" pill, which was
            never meant to sit on a one-animal listing. */}
        {isSingleAnimalListing ? null : availabilityPill ? (
          <span className="listing-availability-pill">{availabilityPill}</span>
        ) : null}
        {cardGallery.length > 1 ? (
          <PhotoCarousel images={cardGallery} alt={title} hoverAdvance imgClassName="variation-image card-img-top" />
        ) : (
          <img
            src={variationImageSrc || NO_PHOTO_SRC}
            data-no-photo={variationImageSrc ? undefined : '1'}
            alt={formatSpeciesName(product?.species, { withLocality: false }) || title}
            className="variation-image card-img-top"
            loading="lazy"
            onError={retryThenPlaceholder}
          />
        )}
      </div>
      <div className={`card-body variation-card-body ${isAvailable ? '' : 'unavailable'}`}>
        <h5 className="card-title mb-2">
          <strong>{title}</strong>
        </h5>
        <p className="card-text mb-1">
          {variation.promo_display?.display_mode === 'per_unit_price' && variation.promo_display?.promo_price != null ? (
            <>
              <span className="price-original">{price}</span>{' '}
              <span className="price-promo">${parseFloat(variation.promo_display.promo_price).toFixed(2)}</span>
              {variation.promo_display.label_used && (
                <span className="price-badge-sale ms-1">{variation.promo_display.label_used}</span>
              )}
              {/* #67 (operator decision 2026-08-25): show the OTHER offers
                  too. Hiding them meant a shopper who saw an offer advertised
                  elsewhere found it missing here and had no idea why — more
                  questions, not fewer. They stay muted and non-interactive:
                  we apply the best discount automatically, so there is
                  nothing to choose and no checkbox. The title explains it on
                  hover/tap. */}
              {(variation.promo_display.promo_labels || [])
                .filter((lbl) => lbl && lbl !== variation.promo_display.label_used)
                .map((lbl, i) => (
                  <span
                    key={`alt-${i}`}
                    className="price-badge-alt ms-1"
                    title="Also available on this listing — we applied the better offer for you automatically."
                  >
                    {lbl}
                  </span>
                ))}
            </>
          ) : variation.promo_display?.has_active_promo && variation.promo_display?.promo_labels?.length > 0 ? (
            <>
              <span className="listing-price">{price}</span>{' '}
              {variation.promo_display.promo_labels.map((lbl, i) => (
                <span key={i} className="price-badge-sale ms-1">{lbl}</span>
              ))}
            </>
          ) : (
            <span className="listing-price">{price}</span>
          )}
        </p>
        {/* I-02e #60b — combinability, said out loud where the shopper
            sees the offers. The backend only sets this when there are TWO
            OR MORE offers on the listing (one offer has nothing to combine
            with), so it never nags on an ordinary sale card. Finding out
            at checkout that two badges didn't add up is the bad version of
            this. */}
        {variation.promo_display?.exclusivity_note && (
          <p
            className="card-text mb-2"
            style={{
              fontSize: '0.75rem',
              color: 'var(--ff-text-secondary, #6b7280)',
              fontStyle: 'italic',
            }}
          >
            {variation.promo_display.exclusivity_note}
          </p>
        )}
        {/* Audit item 10a (2026-08-14): the CTA zone (count line +
            qty/Add row vs the single Notify/Sign-Up button) was the
            whole card-height drift in a mixed grid. .listing-cta-slot
            gives every listing card the SAME bottom band (min-height +
            flex-end + margin-top:auto) so cards bottom out level. */}
        <div className="listing-cta-slot">
        {/* Count line renders ONLY for a card you can actually buy from
            (Cowork QA F1). Suppressed — not reworded — on Notify-Me
            cards: any number here is a promise the card can't keep. */}
        {isPurchasable && (
          <p className="card-text mb-2">Count: {remaining} of {variation.count}</p>
        )}
        {/* R-SHIP-AFTER (2026-10-28): "Ships from 4/12/2027" — the soonest
            day this card's stock can ship, sent only while it is ahead. */}
        {isPurchasable && variation.ships_from_note && (
          <p className="card-text mb-2 animal-listing-ships-from" data-listing-ships-from>
            {variation.ships_from_note}
          </p>
        )}
        {isPurchasable ? (
          <>
          {/* #90a — CTA row matches the sibling animal/lot cards' full-
             width 42px btn-blue: qty box beside, button fills the rest,
             both ~44px tall (.variation-cta-row in products.css). Was a
             92x31 btn-sm next to a 60px form-control-sm. */}
          <div className="variation-cta-row">
            <input
              type="number"
              min="1"
              max={remaining}
              value={quantity}
              onChange={(e) => {
                const val = parseInt(e.target.value) || 1;
                setQtyNote(
                  remaining && val > remaining
                    ? `Only ${remaining} available.`
                    : null,
                );
                setQuantity(Math.max(1, Math.min(remaining, val)));
              }}
              className="form-control variation-qty-input"
              aria-label="Quantity"
            />
            {variation.sales_mode === 'individual' ? (
              /* adv-review SF2 (2026-08-14): an individual-mode card
                 cannot transact from here — the add endpoint requires a
                 specific animal (400s), and guest adds created phantom
                 un-checkoutable lines. Link to the product page where
                 the animal cards ARE transactable. */
              <Link
                to={product?.id ? `/frogs/products/${product.id}` : '#'}
                className="ff-btn ff-btn--secondary variation-cta-view"
              >
                View this animal
              </Link>
            ) : (
            <button
              type="button"
              className="ff-btn ff-btn--primary btn-add-to-cart"
              onClick={handleAddToCart}
              disabled={adding}
            >
              {adding ? 'Adding...' : 'Add to Cart'}
            </button>
            )}
          </div>
          {/* Always mounted — see the cart line: a role="status" that
              appears together with its message is announced by nothing. */}
          <div
            className={`cart-quantity-note${qtyNote ? '' : ' cart-quantity-note--empty'}`}
            role="status"
            aria-live="polite"
            data-testid="variation-qty-note"
          >
            {qtyNote || ''}
          </div>
          </>
        ) : isAuthenticated ? (
          /* Design System v1 (Phase 3): Notify me is a SECONDARY — the
             accent is never a button fill (R3), and a sold-out card has
             no primary at all. */
          <button
            type="button"
            className="ff-btn ff-btn--secondary ff-btn--block"
            onClick={handleNotifyMe}
            disabled={notifyLoading}
          >
            {notifyLoading ? 'Updating...' : restockSubscribed ? 'Notifications On \uD83D\uDD14' : 'Notify Me When Available'}
          </button>
        ) : (
          <Link to="/signup" className="ff-btn ff-btn--secondary ff-btn--block">
            Sign Up to Get Notified
          </Link>
        )}
        </div>
      </div>
    </div>

    <ConfirmationModal
      isOpen={Boolean(restockOptedOutPrefsUrl)}
      title="Restock notifications are disabled"
      message="Restock notifications are disabled in your email preferences. Would you like to go to your email preferences to re-enable them?"
      confirmText="Go to Email Preferences"
      cancelText="Not Now"
      onConfirm={handleGoToPreferences}
      onCancel={() => setRestockOptedOutPrefsUrl(null)}
      testId="confirm-restock-opted-out"
    />
    </>
  );
};

export default VariationCard;
